<script setup>
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import WorkforceButton from './WorkforceButton.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  schedules: { type: Array, default: () => [] },
  exceptions: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  agentColors: { type: Object, default: () => ({}) },
  replacingWeekKey: { type: String, default: null },
});

const emit = defineEmits([
  'update:modelValue',
  'create:schedule',
  'edit:schedule',
  'edit:exception',
  'replace:week',
  'assign:coverage',
]);

const { t, locale } = useI18n();

const calendarRef = ref(null);
const selectedUserId = ref('all');
const selectedDate = ref(dayjs().format('YYYY-MM-DD'));
const currentMonth = ref(dayjs().startOf('month'));
const visibleRange = ref({
  start: dayjs().startOf('month').startOf('week'),
  end: dayjs().endOf('month').endOf('week').add(1, 'day'),
});

const parsedLocale = computed(() => {
  const value = String(locale.value || 'en').replace('_', '-');
  return value || 'en';
});

const monthLabel = computed(() => currentMonth.value.format('MMMM YYYY'));

const selectedDateObj = computed(() =>
  dayjs(selectedDate.value || dayjs().format('YYYY-MM-DD')).startOf('day')
);

const selectedWeekDays = computed(() => {
  const start = selectedDateObj.value.startOf('week');
  return Array.from({ length: 7 }, (_, index) => {
    const date = start.add(index, 'day');
    return {
      isoDate: date.format('YYYY-MM-DD'),
      weekday: date.day(),
      isCurrentMonth: date.isSame(currentMonth.value, 'month'),
    };
  });
});

const selectedWeekKey = computed(
  () => selectedWeekDays.value[0]?.isoDate || null
);

const selectedWeekRangeLabel = computed(() => {
  const start = selectedWeekDays.value[0]?.isoDate;
  const end = selectedWeekDays.value[6]?.isoDate;
  if (!start || !end) return '';
  return `${dayjs(start).format('MMM D')} – ${dayjs(end).format('MMM D')}`;
});

const isReplacingSelectedWeek = computed(
  () => props.replacingWeekKey === selectedWeekKey.value
);

const userMatchesFilter = userId => {
  if (selectedUserId.value === 'all') return true;
  return String(userId) === String(selectedUserId.value);
};

const exceptionsByDate = computed(() => {
  const index = {};
  (props.exceptions || []).forEach(item => {
    if (!item?.date) return;
    if (!userMatchesFilter(item.user_id)) return;
    if (!index[item.date]) {
      index[item.date] = [];
    }
    index[item.date].push(item);
  });
  return index;
});

const schedulesByWeekday = computed(() => {
  const index = Array.from({ length: 7 }, () => []);
  (props.schedules || []).forEach(item => {
    const weekday = Number(item.weekday_index);
    if (Number.isNaN(weekday) || weekday < 0 || weekday > 6) return;
    if (!item.is_active) return;
    if (!userMatchesFilter(item.user_id)) return;
    index[weekday].push(item);
  });
  return index;
});

const parseTimeToMinutes = value => {
  if (!value) return null;
  const [hoursPart, minutesPart] = String(value).split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart || 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

const rangeFromDateAndTime = (date, startTime, endTime) => {
  const base = dayjs(date).startOf('day');
  if (!base.isValid()) return null;

  if (!startTime && !endTime) {
    return {
      start: base,
      end: base.add(1, 'day'),
      allDay: true,
    };
  }

  const startMinutes = parseTimeToMinutes(startTime || '00:00');
  const endMinutes = parseTimeToMinutes(endTime || '24:00');
  if (startMinutes === null || endMinutes === null) return null;

  const start = base.add(startMinutes, 'minute');
  let end = base.add(endMinutes, 'minute');
  if (!end.isAfter(start)) {
    end = end.add(1, 'day');
  }

  return {
    start,
    end,
    allDay: false,
  };
};

const colorFor = item => {
  const option = item.color || props.agentColors?.[Number(item.user_id)];
  return {
    backgroundColor: option?.bgHex || '#111827',
    borderColor: option?.borderHex || option?.bgHex || '#111827',
    textColor: option?.textHex || '#ffffff',
  };
};

const agentNameFor = item => {
  if (item.agent_name) return item.agent_name;
  const agent = props.agents.find(
    user => Number(user.id) === Number(item.user_id)
  );
  return agent?.name || `Agent ${item.user_id}`;
};

const exceptionStatusLabel = status => {
  if (!status) return '';
  const key = String(status).toUpperCase();
  const i18nKey = `WORKFORCE.EXCEPTIONS.STATUS.${key}`;
  const translated = t(i18nKey);
  return translated === i18nKey ? status : translated;
};

const buildEventsForDate = date => {
  const isoDate = date.format('YYYY-MM-DD');
  const weekday = date.day();
  const exceptions = exceptionsByDate.value[isoDate] || [];

  if (exceptions.length) {
    return exceptions
      .map(item => {
        const range = rangeFromDateAndTime(
          isoDate,
          item.start_time,
          item.end_time
        );
        if (!range) return null;
        const color = colorFor(item);
        return {
          id: `exception-${item.id}-${isoDate}`,
          title: `${agentNameFor(item)} · ${exceptionStatusLabel(item.status)}`,
          start: range.start.toISOString(),
          end: range.end.toISOString(),
          allDay: range.allDay,
          backgroundColor: color.backgroundColor,
          borderColor: color.borderColor,
          textColor: color.textColor,
          classNames: ['workforce-fc-event', 'workforce-fc-event-exception'],
          extendedProps: {
            recordType: 'exception',
            raw: item,
          },
        };
      })
      .filter(Boolean);
  }

  return (schedulesByWeekday.value[weekday] || [])
    .map(item => {
      const range = rangeFromDateAndTime(
        isoDate,
        item.start_time,
        item.end_time
      );
      if (!range) return null;
      const color = colorFor(item);
      return {
        id: `schedule-${item.id}-${isoDate}`,
        title: agentNameFor(item),
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        allDay: false,
        backgroundColor: color.backgroundColor,
        borderColor: color.borderColor,
        textColor: color.textColor,
        classNames: ['workforce-fc-event', 'workforce-fc-event-schedule'],
        extendedProps: {
          recordType: 'schedule',
          raw: item,
        },
      };
    })
    .filter(Boolean);
};

const calendarEvents = computed(() => {
  const start = visibleRange.value.start.startOf('day');
  const end = visibleRange.value.end.startOf('day');
  const events = [];

  for (
    let cursor = start;
    cursor.isBefore(end, 'day');
    cursor = cursor.add(1, 'day')
  ) {
    events.push(...buildEventsForDate(cursor));
  }

  return events;
});

const closeModal = () => {
  emit('update:modelValue', false);
};

const syncCurrentMonthFromCalendar = () => {
  const api = calendarRef.value?.getApi();
  if (!api) return;
  currentMonth.value = dayjs(api.getDate()).startOf('month');
};

const goToPreviousMonth = () => {
  const api = calendarRef.value?.getApi();
  if (!api) return;
  api.prev();
  syncCurrentMonthFromCalendar();
};

const goToNextMonth = () => {
  const api = calendarRef.value?.getApi();
  if (!api) return;
  api.next();
  syncCurrentMonthFromCalendar();
};

const goToToday = () => {
  const api = calendarRef.value?.getApi();
  if (!api) return;
  api.today();
  selectedDate.value = dayjs().format('YYYY-MM-DD');
  syncCurrentMonthFromCalendar();
};

const selectDate = dateValue => {
  const parsed = dayjs(dateValue).startOf('day');
  if (!parsed.isValid()) return;
  selectedDate.value = parsed.format('YYYY-MM-DD');
};

const createScheduleForSelectedDate = () => {
  const date = selectedDateObj.value;
  emit('create:schedule', {
    weekday: date.day(),
    date: date.format('YYYY-MM-DD'),
  });
};

const assignCoverageForSelectedDate = () => {
  const date = selectedDateObj.value;
  emit('assign:coverage', {
    weekday: date.day(),
    date: date.format('YYYY-MM-DD'),
  });
};

const replaceSelectedWeek = () => {
  if (!selectedWeekKey.value) return;
  emit('replace:week', {
    weekId: selectedWeekKey.value,
    days: selectedWeekDays.value,
  });
};

const handleEventClick = info => {
  const { recordType, raw } = info.event.extendedProps || {};
  if (!raw) return;

  if (recordType === 'exception') {
    emit('edit:exception', raw);
    return;
  }

  emit('edit:schedule', raw);
};

const handleDateClick = info => {
  const clicked = dayjs(info.date).format('YYYY-MM-DD');
  selectDate(clicked);
  emit('create:schedule', {
    weekday: dayjs(info.date).day(),
    date: clicked,
  });
};

const handleDatesSet = info => {
  const start = dayjs(info.start).startOf('day');
  const end = dayjs(info.end).startOf('day');
  visibleRange.value = { start, end };

  const midPoint = start.add(Math.floor(end.diff(start, 'day') / 2), 'day');
  currentMonth.value = midPoint.startOf('month');

  const selected = selectedDateObj.value;
  if (selected.isBefore(start) || !selected.isBefore(end)) {
    selectedDate.value = midPoint.startOf('day').format('YYYY-MM-DD');
  }
};

const calendarOptions = computed(() => ({
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: 'dayGridMonth',
  initialDate: currentMonth.value.toDate(),
  locale: parsedLocale.value,
  fixedWeekCount: true,
  showNonCurrentDates: true,
  dayMaxEventRows: 3,
  moreLinkClick: 'popover',
  headerToolbar: false,
  selectable: false,
  editable: false,
  eventDisplay: 'block',
  events: calendarEvents.value,
  eventClick: handleEventClick,
  dateClick: handleDateClick,
  datesSet: handleDatesSet,
  dayCellClassNames: args => {
    const date = dayjs(args.date);
    const classes = [];

    if (date.isSame(dayjs(), 'day')) {
      classes.push('workforce-fc-today');
    }
    if (date.format('YYYY-MM-DD') === selectedDate.value) {
      classes.push('workforce-fc-selected-day');
    }

    return classes;
  },
  height: 'auto',
}));

watch(
  () => props.modelValue,
  isOpen => {
    if (!isOpen) return;
    const api = calendarRef.value?.getApi();
    if (!api) return;
    api.gotoDate(selectedDateObj.value.toDate());
    syncCurrentMonthFromCalendar();
  }
);
</script>

<template>
  <transition
    enter-active-class="transition-opacity duration-200"
    leave-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4"
      @click.self="closeModal"
    >
      <div
        class="flex flex-col bg-n-solid-1 rounded-2xl shadow-2xl border border-n-slate-300 w-[98vw] h-[96vh] max-w-[1800px] dark:border-n-slate-700"
        @click.stop
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-slate-300 px-6 py-4 dark:border-n-slate-700"
        >
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="text-xl font-bold text-n-slate-12">
              {{ t('WORKFORCE.SCHEDULES.MONTHLY_VIEW.TITLE') }}
            </h2>
            <WorkforceButton
              size="xs"
              variant="ghost"
              icon="i-lucide-chevron-left"
              @click="goToPreviousMonth"
            />
            <span
              class="min-w-[180px] text-center text-sm font-semibold text-n-slate-12"
            >
              {{ monthLabel }}
            </span>
            <WorkforceButton
              size="xs"
              variant="ghost"
              icon="i-lucide-chevron-right"
              @click="goToNextMonth"
            />
            <WorkforceButton size="xs" variant="ghost" @click="goToToday">
              {{ t('WORKFORCE.SCHEDULES.GO_TODAY') }}
            </WorkforceButton>
          </div>

          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-2 text-xs text-n-slate-10">
              {{ t('WORKFORCE.FILTERS.AGENT') }}
              <select
                v-model="selectedUserId"
                class="rounded-lg border border-n-slate-300 bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 dark:border-n-slate-700"
              >
                <option value="all">
                  {{ t('WORKFORCE.FILTERS.ALL_AGENTS') }}
                </option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="String(agent.id)"
                >
                  {{ agent.name }}
                </option>
              </select>
            </label>

            <WorkforceButton
              size="xs"
              icon="i-lucide-x"
              variant="ghost"
              @click="closeModal"
            >
              {{ t('WORKFORCE.ACTIONS.CLOSE') }}
            </WorkforceButton>
          </div>
        </div>

        <div class="flex-1 overflow-auto p-6">
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <span
              class="rounded-full bg-n-solid-2 px-3 py-1 text-[11px] font-semibold text-n-slate-10"
            >
              {{
                t('WORKFORCE.SCHEDULES.MONTHLY_VIEW.WEEK_LABEL', {
                  range: selectedWeekRangeLabel,
                })
              }}
            </span>
            <WorkforceButton
              size="xs"
              color="slate"
              icon="i-lucide-rotate-cw"
              :is-loading="isReplacingSelectedWeek"
              @click="replaceSelectedWeek"
            >
              {{ t('WORKFORCE.SCHEDULES.MONTHLY_VIEW.REPLACE_WEEK') }}
            </WorkforceButton>
            <WorkforceButton
              size="xs"
              variant="ghost"
              color="onyx"
              icon="i-lucide-plus"
              @click="createScheduleForSelectedDate"
            >
              {{ t('WORKFORCE.SCHEDULES.WEEKLY.ADD_SLOT') }}
            </WorkforceButton>
            <WorkforceButton
              size="xs"
              variant="ghost"
              color="onyx"
              icon="i-lucide-users"
              @click="assignCoverageForSelectedDate"
            >
              {{ t('WORKFORCE.SCHEDULES.WEEKLY.ASSIGN_DAY') }}
            </WorkforceButton>
          </div>

          <div class="workforce-calendar-wrapper">
            <FullCalendar ref="calendarRef" :options="calendarOptions" />
          </div>
        </div>

        <div
          class="border-t border-n-slate-300 px-6 py-3 dark:border-n-slate-700"
        >
          <p class="text-xs text-n-slate-10">
            {{ t('WORKFORCE.SCHEDULES.MONTHLY_VIEW.HINT') }}
          </p>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.workforce-calendar-wrapper {
  border: 1px solid rgba(71, 85, 105, 0.45);
  border-radius: 0.75rem;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.12);
}

:deep(.fc) {
  --fc-border-color: rgba(100, 116, 139, 0.35);
  --fc-page-bg-color: transparent;
  --fc-neutral-bg-color: rgba(15, 23, 42, 0.24);
  font-size: 12px;
}

:deep(.fc .fc-toolbar.fc-header-toolbar) {
  display: none;
}

:deep(.fc .fc-col-header-cell) {
  background: rgba(30, 41, 59, 0.42);
}

:deep(.fc .fc-col-header-cell-cushion) {
  padding: 10px 8px;
  font-weight: 600;
  color: rgb(226, 232, 240);
}

:deep(.fc .fc-daygrid-day) {
  background: rgba(15, 23, 42, 0.12);
}

:deep(.fc .fc-daygrid-day.workforce-fc-today) {
  background: rgba(51, 65, 85, 0.35);
}

:deep(.fc .fc-daygrid-day.workforce-fc-selected-day) {
  box-shadow: inset 0 0 0 2px rgba(15, 23, 42, 0.72);
}

:deep(.dark .fc .fc-daygrid-day.workforce-fc-selected-day) {
  box-shadow: inset 0 0 0 2px rgba(226, 232, 240, 0.7);
}

:deep(.fc .fc-daygrid-day-top) {
  justify-content: space-between;
}

:deep(.fc .fc-daygrid-day-number) {
  color: rgb(148, 163, 184);
  font-size: 11px;
  font-weight: 600;
  padding: 6px;
}

:deep(.fc .fc-daygrid-event) {
  border-radius: 8px;
  border-width: 1px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.26);
}

:deep(.workforce-fc-event .fc-event-main) {
  padding: 2px 5px;
}

:deep(.workforce-fc-event .fc-event-title) {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
}

:deep(.workforce-fc-event-exception) {
  border-style: dashed;
}
</style>
